/**
 * Reservation guard — operator-set invariant 2026-05-11.
 *
 * AT MOST ONE `reserved` eSIM per user at any time. Multiple reservations
 * have no UX value (user can only pay one at a time, and each holds a pool
 * row hostage for the 30-min TTL). Every CTA that COULD create a new
 * reservation must check first and either proceed or bounce the user to
 * their existing reservation's "Pay now" card.
 *
 * Sites enforcing this rule:
 *   - main.ts  `get-esim`        (country page CTA)
 *   - main.ts  `add-new-esim`    (dashboard "+ Add eSIM" tile)
 *   - dashboard.ts `resumePendingEsim`  (post-login auto-open)
 *   - esim-picker disables the "+ New eSIM" row visually when reserved
 *     exists (caller pre-fetched the same data — soft guard there is fine)
 *
 * Backend `reserveEsim()` ALSO enforces this as a DB-level invariant
 * (mig-free, just a SELECT before the pool claim). That's the bottom
 * line of defence — even if frontend bypasses, backend rejects with
 * 409 `esim_already_reserved`. Holistic fix per PROJECT-CONFIG rule.
 *
 * Why fetch /api/esims on every CTA click rather than reading from a
 * cache: the cache could be stale (user reserved on device A, opened
 * device B). One round-trip per click is cheap; the alternative
 * (background poll + cache) is more code and still has a stale window.
 * The 60s dashboard auto-refresh already burns this budget — one more
 * fetch per CTA tap is in the noise.
 */
import { showToast } from './toast';
import { t } from './i18n';
import { Router } from './router';

export async function assertNoActiveReservation(): Promise<boolean> {
  try {
    const { fetchEsims } = await import('./api');
    const { esims } = await fetchEsims();
    const reserved = esims.find((e) => e.status === 'reserved');
    if (!reserved) return true;

    // Choose the toast variant. `alreadyReservedWithPay` references the
    // Pay-now CTA on the reserved card (used when we have an invoice URL
    // to resume); `alreadyReserved` is the generic version. Backend fills
    // `pendingInvoiceUrl` on every reserved eSIM whose payment_intent
    // still has a usable NowPayments invoice URL.
    const msg = reserved.pendingInvoiceUrl
      ? t('dash.esims.alreadyReservedWithPay')
      : t('dash.esims.alreadyReserved');
    showToast(msg, 'info', 7000);

    // Move user to where the reserved card lives, so "Pay now" is one
    // tap away. Router.navigate is idempotent when target === current,
    // so this is a no-op on the dashboard itself.
    Router.navigate('/dashboard/esims');

    return false;
  } catch {
    // Network failure: be permissive — let the user attempt to create,
    // backend will reject with 409 `esim_already_reserved` if needed.
    // Better UX than blocking on a transient failure.
    return true;
  }
}
